import React, { useState } from "react";
import { Trash, CaretDown } from "@phosphor-icons/react";
import CatIcon from "./CatIcon";
import { renderEntryLines, renderEntryDetails } from "./vaultSchema";

/**
 * Expandable vault entry card. Header shows the masked summary; tap (or
 * Enter/Space) to reveal full unmasked details + larger ID photo. The trash
 * button is isolated via e.stopPropagation so deleting never toggles.
 */
export default function EntryCard({ cat, entry, familyMembers, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const lines = renderEntryLines(cat.id, entry, familyMembers);
  const details = renderEntryDetails(cat.id, entry, familyMembers);
  const hasPhoto = cat.id === "photo_id" && entry.photo;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setExpanded((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setExpanded((v) => !v);
        }
      }}
      aria-expanded={expanded}
      data-testid="entry-card"
      className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 cursor-pointer active:bg-bone/30 transition-colors focus:outline-none focus:ring-2 focus:ring-navy/30"
    >
      <div className="flex items-start gap-3">
        {hasPhoto ? (
          <img
            src={entry.photo}
            alt=""
            className="shrink-0 w-14 h-14 rounded-xl object-cover border border-navy/10"
          />
        ) : cat.buttonSrc ? (
          // 3D steel-frame neon button — consistent with the empty-state
          // hero card and the main vault grid. No pastel chip wrapper:
          // the button artwork already includes its own steel frame and
          // glow halo.
          <img
            src={cat.buttonSrc}
            alt=""
            aria-hidden
            draggable={false}
            className="shrink-0 w-12 h-12 object-contain"
          />
        ) : (
          <div
            className="shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: cat.soft }}
          >
            <CatIcon cat={cat} size={20} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {lines.map((l, i) => (
            <div
              key={i}
              className={
                i === 0
                  ? "font-jakarta font-extrabold text-navy text-base leading-tight"
                  : "text-navy-soft text-sm font-jakarta font-medium mt-0.5"
              }
            >
              {l}
            </div>
          ))}
        </div>

        <div className="shrink-0 flex flex-col items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label="Remove"
            data-testid="entry-remove-btn"
            className="w-9 h-9 rounded-full hover:bg-bone/70 flex items-center justify-center"
          >
            <Trash size={18} weight="duotone" color="#9CA3AF" />
          </button>
          <CaretDown
            size={16}
            weight="bold"
            color="#0E2C54"
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 200ms",
              opacity: 0.55,
            }}
          />
        </div>
      </div>

      {expanded && (
        <div
          className="mt-4 pt-4 border-t border-navy/10"
          data-testid="entry-card-expanded"
        >
          {hasPhoto && (
            <img
              src={entry.photo}
              alt="ID"
              className="w-full max-h-72 object-contain rounded-xl border border-navy/10 mb-3 bg-bone/40"
            />
          )}
          <dl className="space-y-2">
            {details.map((d, i) => (
              <div key={d.label} className="flex items-baseline gap-3">
                <dt className="text-navy-soft text-[11px] font-jakarta font-bold uppercase tracking-wider w-28 shrink-0">
                  {d.label}
                </dt>
                <dd
                  className="text-navy text-sm font-jakarta font-semibold break-words"
                  data-testid={`entry-detail-${d.label
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")}`}
                >
                  {d.value}
                </dd>
              </div>
            ))}
          </dl>
          {entry.note && (
            <p className="mt-3 text-navy-soft text-sm font-jakarta italic">
              {entry.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
